import { Link } from 'react-router'
import { AngledBand } from '@carouselgg/nexus/angled-band'
import { DisplayHeading } from '@carouselgg/nexus/display-heading'
import { ScrollReveal } from '@carouselgg/nexus/scroll-reveal'
import { Check, ArrowRight, LayoutGrid, User, GraduationCap } from '@carouselgg/nexus/icon'
import { useTrackVisibility } from '../hooks/useTrackVisibility'
import styles from './Audiences.module.css'

const AUDIENCES = [
  {
    kicker: 'For organizers',
    title: 'Run a league people finish.',
    accent: 'var(--nexus-purple)',
    icon: LayoutGrid,
    points: [
      'Screen applicants by Trust Score before the split starts',
      'Scheduling, standings and playoff brackets that generate themselves',
      'A white-labelled public page under your own brand',
    ],
    cta: { label: 'Run a league', to: '/for/leagues' },
  },
  {
    kicker: 'For players',
    title: 'Games you can actually win.',
    accent: 'var(--nexus-pink)',
    icon: User,
    points: [
      'Matches against teams at your rank — not whoever happened to enter',
      'Same squad, every week, all season. Tournaments when you want the pressure.',
      'One verified identity across every platform, and a record that follows you',
    ],
    cta: { label: 'Find a league', to: '/compete' },
  },
  {
    kicker: 'For universities',
    title: 'A program that outlasts the seniors.',
    accent: 'var(--nexus-blue)',
    icon: GraduationCap,
    // LMS sync, academic eligibility and scholarship tooling do not exist —
    // the API returns empty arrays for all three. They are listed as roadmap
    // on /for/universities instead of claimed as features here.
    points: [
      'Run campus seasons with real scheduling, standings and playoffs',
      'Rank ranges and Trust Score floors keep smurfs out of your bracket',
      'Rosters and results live on the program page, not a graduating senior’s Discord',
    ],
    cta: { label: 'For campus programs', to: '/for/universities' },
  },
]

export function Audiences() {
  const ref = useTrackVisibility<HTMLDivElement>('audiences')

  return (
    <AngledBand id="audiences" tone="elevated" slice="both">
      <div className={styles.inner} ref={ref}>
        <ScrollReveal>
          <div className={styles.head}>
            <div>
              <p className={styles.eyebrow}>
                <span className={styles.seed}>05</span>
                <span className={styles.rule} />
                Who it is for
              </p>
              <DisplayHeading size="6xl">Built for all three.</DisplayHeading>
            </div>
            <p className={styles.lede}>
              Organizers get the tooling. Players get an identity that travels with them.
              Universities get eligibility, scouting and scholarship pipelines that hold up to
              scrutiny.
            </p>
          </div>
        </ScrollReveal>

        <div className={styles.grid}>
          {AUDIENCES.map((audience, index) => {
            const Icon = audience.icon
            return (
              <ScrollReveal key={audience.kicker} delay={index * 90}>
                <div
                  className={styles.card}
                  style={{ ['--card-accent' as string]: audience.accent }}
                >
                  <div className={styles.cardTop}>
                    <Icon size={24} />
                    <span className={styles.cardKicker}>{audience.kicker}</span>
                  </div>

                  <DisplayHeading as="h3" size="3xl" cut="soft">
                    {audience.title}
                  </DisplayHeading>

                  <div className={styles.points}>
                    {audience.points.map((point) => (
                      <p key={point} className={styles.point}>
                        <Check size={15} className={styles.tick} />
                        {point}
                      </p>
                    ))}
                  </div>

                  <Link to={audience.cta.to} className={styles.cardLink}>
                    {audience.cta.label}
                    <ArrowRight size={14} />
                  </Link>
                </div>
              </ScrollReveal>
            )
          })}
        </div>
      </div>
    </AngledBand>
  )
}
